import React from 'react';
import { Outlet, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ADVISOR_LOGIN_PATH, loginPathForPath } from '../lib/authPaths';

/**
 * ProtectedRoute — real auth gate.
 * Checks session, validates role, redirects to the correct *door* if
 * unauthorized — clients land on the client suite access page, not the
 * studio door, and vice-versa.  Wave 12.x security separation.
 *
 * Feb 2026 — Client redirects are now slug-aware via loginPathForPath.
 * A protected client route like `/suite/ifearulundu/portal` bounces
 * back to `/suite/ifearulundu/access` rather than the old hardcoded
 * Jackson fallback.  `/portal/client` (no slug context) lands on the
 * NEUTRAL access page.
 */
export function ProtectedRoute({ requiredRole }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAFAF8]" data-testid="auth-loading">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-[#1A1A1A] border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-sm text-[#6B6B6B]">Verifying session...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    const target = requiredRole === 'client'
      ? loginPathForPath(location.pathname)
      : ADVISOR_LOGIN_PATH;
    return <Navigate to={target} state={{ from: location.pathname }} replace />;
  }

  // Role check — owner can access ALL portals
  const allowedRoles = Array.isArray(requiredRole) ? requiredRole : [requiredRole];
  if (user.role === 'owner') {
    return <Outlet />;
  }
  if (!allowedRoles.includes(user.role)) {
    // Wrong role — send them to their own portal entry.
    if (user.role === 'client') return <Navigate to="/portal/client" replace />;
    return <Navigate to="/portal/advisor" replace />;
  }

  return <Outlet />;
}
